import { CheckInsMyMSCResponse } from '@msc-b2c/interfaces/interfaces';
import { getWciClosesOn, getWciOpensOn } from '@msc-b2c/shared-utils';
import dayjs from 'dayjs';

/**
 * Determines whether the web check-in process is complete for all passengers.
 *
 * This checks if all entries in the `checkIns` array have `isCompleted === true`.
 *
 * @returns {boolean} True if all passengers have completed their check-in, false otherwise.
 */
export const isStatusComplete = (
  checkInStatus: CheckInsMyMSCResponse | null | undefined
): boolean => {
  if (!checkInStatus || !checkInStatus?.checkIns || checkInStatus?.checkIns.length === 0)
    return false;

  return checkInStatus.checkIns.every((checkin) => checkin.isCompleted);
};

/**
 * Returns true if we're currently within the valid check-in period
 */
export const isDaysOfCheckinPassed = (daysBeforeDeparture: number): boolean => {
  const wciCloseOn = getWciClosesOn();
  return wciCloseOn ? daysBeforeDeparture >= wciCloseOn : false;
};

/**
 * Determines whether the current date is within the allowed check-in window
 * based on the number of days before departure.
 * @param {number} daysBeforeDeparture - The number of days between today and the departure date.
 * @returns {boolean} True if the current date falls within the check-in window, false otherwise.
 */
export const isWithinCheckInWindow = (daysBeforeDeparture: number): boolean => {
  const opensOn = getWciOpensOn();
  const closesOn = getWciClosesOn();

  if (opensOn === undefined || closesOn === undefined) return false;

  return daysBeforeDeparture <= opensOn && daysBeforeDeparture >= closesOn;
};

/**
 * Determines if the button should be active (enabled)
 * based on date conditions
 */
export const isActive = (daysBeforeDeparture: number): boolean => {
  return isWithinCheckInWindow(daysBeforeDeparture);
};

/**
 * Calculates the check-in start date by subtracting a specified number of days
 * from the cruise departure date.
 *
 * @param {string} departureDate - The ISO string representation of the cruise departure date.
 * @param {number} daysBefore - The number of days before the departure when check-in opens.
 * @returns {{day: string, month: string, year: string, date: Date}}
 *          An object representing the check-in start date, with day zero-padded and month
 *          formatted as a full name (locale-independent), plus the raw `date` used to build
 *          a custom formatted date if needed.
 *
 * Example:
 *  Input: '2025-06-15', 5
 *  Output: { day: '10', month: 'June', year: '2025', date: Date }
 */
export function getCheckinStartDate(departureDate: string) {
  const date = new Date(departureDate);
  date.setDate(date.getDate() - (getWciOpensOn() ?? 0));
  const parsedDate = dayjs(date);

  return {
    day: parsedDate.format('DD'),
    month: parsedDate.format('MMMM'),
    year: parsedDate.format('YYYY'),
    date: parsedDate,
  };
}

/**
 * Formats the balance due date into an object containing day, month, and year.
 *
 * @param {string} balanceDueDate - The ISO string representation of the balance due date.
 * @returns {{day: string, month: string, year: string}}
 *          An object representing the balance due date, with the day zero-padded
 *          and the month formatted as a full name (locale-independent).
 */
export function getBalanceDueDate(balanceDueDate: string) {
  const date = new Date(balanceDueDate);
  const parsedDate = dayjs(date);

  return {
    day: parsedDate.format('DD'),
    month: parsedDate.format('MMMM'),
    year: parsedDate.format('YYYY'),
  };
}
